import React, { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormCard } from '@features/onboarding/components/shared/form-card/FormCard';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import {
  getApplicantRole,
  getCoverageTagForApplicantIndex,
} from '@features/onboarding/utils/coverageLevel';
import { useApplicationStore } from 'src/store/application';
import { useInsuranceStore } from 'src/store/insurance';

import { Checkbox } from '@components/ui/checkbox';
import { Label } from '@components/ui/label';

interface CoverageTagsProps {
  form: UseFormReturn<Record<string, unknown>>;
  extraDependents: Array<{ firstName?: string; applicant: string }>;
  planType: string; // 'medical', 'dental', 'vision', etc.
  showTags: boolean;
  categoryPlans?: Array<{ id: string; category?: string }>; // For non-medical plans
}

export const CoverageTags = ({
  form,
  extraDependents,
  planType,
  showTags,
  categoryPlans = [],
}: CoverageTagsProps) => {
  const { data, setData, setMedicalTags, setPlanTags, medicalTags, planTags } =
    useOnboardingStore();
  const { activeCategories, isMainPlanRejected, applications } =
    useApplicationStore();
  const { plans } = useInsuranceStore();

  const categoryKey =
    planType?.toLowerCase().replace(/[^a-zA-Z0-9]/g, '') || '';
  const planKey = `${categoryKey}Plan`;
  const selectedNonMedicalPlanId = form.getValues(planKey) as unknown as
    | string
    | undefined;
  const planTagsForm =
    (form.watch('planTags') as unknown as Record<string, string[]>) || {};

  // Map local dependent index -> global applicant index in application.applicants
  const getGlobalApplicantIndex = (depIdx: number): number | null => {
    const app = applications?.[0] as any;
    const appApplicants: Array<{
      applicant?: string | number;
      firstName?: string;
    }> = app?.applicants || [];
    const dep = extraDependents[depIdx];
    if (!dep) return null;
    const depRole = getApplicantRole(dep.applicant);

    // Try exact match by role and firstName
    const exactIdx = appApplicants.findIndex(
      (a) =>
        getApplicantRole(a?.applicant) === depRole &&
        (!!dep.firstName ? a?.firstName === dep.firstName : true)
    );
    if (exactIdx >= 0) return exactIdx;

    // Fallback: role-based occurrence order among dependents
    if (dep.applicant === 'spouse') {
      const spouseIdx = appApplicants.findIndex(
        (a) => getApplicantRole(a?.applicant) === 'spouse'
      );
      if (spouseIdx >= 0) return spouseIdx;
    }
    if (dep.applicant === 'dependent') {
      // Count how many dependents are before this one in extraDependents
      const dependentOccurence = extraDependents
        .slice(0, depIdx + 1)
        .filter((d) => d.applicant === 'dependent').length;

      // Now find the nth dependent in the main application list
      let count = 0;
      for (let i = 0; i < appApplicants.length; i++) {
        if (getApplicantRole(appApplicants[i]?.applicant) === 'dependent') {
          count++;
          if (count === dependentOccurence) return i;
        }
      }
    }
    return null;
  };

  const tagsFromDependents = React.useMemo(
    () =>
      extraDependents.map((dep, index) => {
        const mapped = getGlobalApplicantIndex(index);
        const gid = mapped != null ? mapped : index + 1;
        const name = dep.firstName || `Dependent ${index + 1}`;
        const role = dep.applicant === 'spouse' ? 'spouse' : 'dependent';
        return {
          label: `${name} (${role})`,
          value: `${dep.applicant}_${gid}`,
        };
      }),
    [extraDependents, applications]
  );
  const defaultTagValues = React.useMemo(
    () => tagsFromDependents.map((tag) => tag.value),
    [tagsFromDependents]
  );
  const defaultTagValueKey = defaultTagValues.join('|');

  // Initialize local state for checked tags as a map (keys must match tagsFromDependents values)
  const [checkedTags, setCheckedTags] = useState<Record<string, boolean>>(
    tagsFromDependents.reduce(
      (acc, tag) => {
        acc[tag.value] = false;
        return acc;
      },
      {} as Record<string, boolean>
    )
  );

  const hasAnyActiveExtraPlan = activeCategories.some(
    (cat) => cat !== 'Major Medical'
  );

  const isPlanBlocked = () => {
    if (planType === 'medical') {
      return (
        Boolean(isMainPlanRejected) ||
        activeCategories.includes('Major Medical')
      );
    }
    return hasAnyActiveExtraPlan;
  };
  const planBlocked = isPlanBlocked();

  const medicalTagsForm =
    (form.watch('medicalTags') as unknown as string[]) || [];

  useEffect(() => {
    if (!showTags || planBlocked || defaultTagValues.length === 0) return;

    if (planType === 'medical') {
      if (Object.prototype.hasOwnProperty.call(data, 'medicalTags')) return;

      form.setValue('medicalTags', defaultTagValues, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setData({ medicalTags: defaultTagValues }, { skipFormReset: true });
      return;
    }

    if (categoryPlans.length === 0) return;

    const currentPlanTags =
      (form.getValues('planTags') as unknown as Record<string, string[]>) || {};
    const storePlanTags = data.planTags || {};
    const updatedPlanTags: Record<string, string[]> = {
      ...currentPlanTags,
    };
    let hasMissingPlanTags = false;

    categoryPlans.forEach((plan) => {
      const hasFormTags = Object.prototype.hasOwnProperty.call(
        currentPlanTags,
        plan.id
      );
      const hasStoreTags = Object.prototype.hasOwnProperty.call(
        storePlanTags,
        plan.id
      );

      if (!hasFormTags && !hasStoreTags) {
        updatedPlanTags[plan.id] = defaultTagValues;
        hasMissingPlanTags = true;
      }
    });

    if (!hasMissingPlanTags) return;

    form.setValue('planTags', updatedPlanTags, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setData({ planTags: updatedPlanTags }, { skipFormReset: true });
    form.trigger();
  }, [
    categoryPlans,
    data,
    defaultTagValueKey,
    defaultTagValues,
    form,
    planBlocked,
    planType,
    setData,
    showTags,
  ]);

  useEffect(() => {
    if (!showTags) return;

    const sourceTags =
      planType === 'medical'
        ? medicalTagsForm
        : (() => {
            const sourcePlanId =
              selectedNonMedicalPlanId || categoryPlans[0]?.id || null;
            return sourcePlanId ? planTagsForm[sourcePlanId] || [] : [];
          })();

    const nextChecked: Record<string, boolean> = tagsFromDependents.reduce(
      (acc, tag) => {
        acc[tag.value] = sourceTags.includes(tag.value);
        return acc;
      },
      {} as Record<string, boolean>
    );

    const keysMatch =
      Object.keys(nextChecked).length === Object.keys(checkedTags).length;
    const valuesMatch = tagsFromDependents.every(
      (tag) => checkedTags[tag.value] === nextChecked[tag.value]
    );

    if (!keysMatch || !valuesMatch) {
      setCheckedTags(nextChecked);
    }
  }, [
    planTagsForm,
    medicalTagsForm,
    selectedNonMedicalPlanId,
    tagsFromDependents,
    categoryPlans,
    planType,
    showTags,
    checkedTags,
  ]);

  const handleTagChange = (itemValue: string, checked: boolean) => {
    if (planBlocked) return;

    const nextCheckedTags: Record<string, boolean> = {
      ...checkedTags,
      [itemValue]: checked,
    };
    setCheckedTags(nextCheckedTags);

    // Sync with form and store (maintain existing behavior)
    if (planType === 'medical') {
      const current =
        (form.getValues('medicalTags') as unknown as string[]) || [];
      const newTags = checked
        ? [...current, itemValue]
        : current.filter((val: string) => val !== itemValue);

      form.setValue('medicalTags', newTags);
      setMedicalTags(newTags);
    } else {
      const currentPlanTags =
        (form.getValues('planTags') as unknown as Record<string, string[]>) ||
        {};

      const selectedTagValues = Object.entries(nextCheckedTags)
        .filter(([, isChecked]) => isChecked)
        .map(([tagValue]) => tagValue);

      const updatedPlanTags: Record<string, string[]> = {
        ...currentPlanTags,
      };

      categoryPlans.forEach((plan) => {
        updatedPlanTags[plan.id] = selectedTagValues;
        setPlanTags(plan.id, selectedTagValues);
      });

      form.setValue('planTags', updatedPlanTags, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      form.trigger();
    }
  };

  // getGlobalApplicantIndex defined above
  // const activeGlobalIdsForDebug = useMemo(() => {
  //   const ids: number[] = [];
  //   tagsFromDependents.forEach((tag, idx) => {
  //     if (checkedTags[tag.value]) { // Use checkedTags state
  //       const mapped = getGlobalApplicantIndex(idx);
  //       const gid = mapped != null ? mapped : idx + 1; // fallback: primary=0, dependents start at 1
  //       ids.push(gid);
  //     }
  //   });
  //   return ids;
  // }, [tagsFromDependents, extraDependents, applications, checkedTags]);

  // Seed checked states from application plans on mount/when selection changes
  useEffect(() => {
    const app = applications?.[0] as any;
    if (!app) return;

    const appPlans: any[] = app.plans || [];

    let applicantIdxs: number[] = [];
    if (planType === 'medical') {
      const medicalPlan = appPlans.find((p) => {
        const details = plans.find((pl) => String(pl.id) === String(p.id));
        return details?.category === 'Major Medical';
      });
      applicantIdxs = Array.isArray(medicalPlan?.applicants)
        ? medicalPlan.applicants
        : [];
    } else {
      if (selectedNonMedicalPlanId) {
        const idxSelectedNonMedicalPlanId = plans.find(
          (e) => e.uid === selectedNonMedicalPlanId
        )?.id;
        const selected = appPlans.find(
          (p) => String(p.id) === String(idxSelectedNonMedicalPlanId)
        );
        applicantIdxs = Array.isArray(selected?.applicants)
          ? selected.applicants
          : [];
      } else {
        const idxSet = new Set<number>();
        categoryPlans.forEach((cp) => {
          const found = appPlans.find((p) => String(p.id) === String(cp.id));
          (Array.isArray(found?.applicants) ? found.applicants : []).forEach(
            (i: number) => idxSet.add(i)
          );
        });
        applicantIdxs = Array.from(idxSet);
      }
    }

    const appApplicants = app?.applicants || [];
    const mapApplicantIdxs = applicantIdxs
      .map((applicantIndex) =>
        getCoverageTagForApplicantIndex(appApplicants, applicantIndex)
      )
      .filter((tag): tag is string => !!tag);
    mapApplicantIdxs.forEach((e) => {
      setCheckedTags((prev) => ({
        ...prev,
        [`${e}`]: true,
      }));
    });
  }, [applications]);

  if (!showTags || planBlocked) return null;

  const getTitle = () => {
    switch (planType) {
      case 'medical':
        return 'Who do you want covered on this plan besides yourself?';
      case 'dental':
        return 'Who do you want covered on this dental plan besides yourself?';
      case 'vision':
        return 'Who do you want covered on this vision plan besides yourself?';
      case 'accident':
        return 'Who do you want covered on this accident plan besides yourself?';
      case 'critical illness':
        return 'Who do you want covered on this critical illness plan besides yourself?';
      default:
        return `Who do you want covered on this ${planType} plan besides yourself?`;
    }
  };

  return (
    <FormCard imageSrc="/images/dependents.svg" title={getTitle()}>
      <div className="flex gap-5 items-center max-2xs:flex-col max-2xs:items-start max-2xs:gap-2">
        {/* <pre>{JSON.stringify(activeGlobalIdsForDebug, null, 2)}</pre> */}
        <p className="font-bold text-xl max-2xs:text-lg min-w-fit">You and</p>
        <div className="grid gap-x-16 grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 pr-4">
          {tagsFromDependents.map((item) => (
            <div key={item.value} className="flex items-center gap-2">
              <Checkbox
                id={`${planType}_${item.value}`}
                checked={checkedTags[item.value]} // Use checkedTags state
                onCheckedChange={(checked) =>
                  handleTagChange(item.value, !!checked)
                }
                className={planBlocked ? 'hidden' : ''}
              />
              <Label htmlFor={`${planType}_${item.value}`}>{item.label}</Label>
            </div>
          ))}
        </div>
      </div>
    </FormCard>
  );
};
